import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useGetCommissionDetailsQuery } from '../services/ledger/ledgerApi';
import Loading from '../components/Loading';

const EmptyState = () => (
  <div className="flex flex-col items-center justify-center py-8 text-gray-400">
    <svg className="w-16 h-10 mb-2" viewBox="0 0 64 41" fill="none">
      <ellipse cx="32" cy="33" rx="32" ry="7" fill="#f5f5f5" />
      <path
        d="M55 12.76L44.854 1.258C44.367.474 43.656 0 42.907 0H21.093c-.749 0-1.46.474-1.947 1.257L9 12.761V22h46v-9.24z"
        fill="#fafafa"
        stroke="#d9d9d9"
      />
      <path
        d="M41.613 15.931c0-1.605.994-2.93 2.227-2.931H55v18.137C55 33.26 53.68 35 52.05 35h-40.1C10.32 35 9 33.259 9 31.137V13h11.16c1.233 0 2.227 1.323 2.227 2.928v.022c0 1.605 1.005 2.901 2.237 2.901h14.752c1.232 0 2.237-1.308 2.237-2.913v-.007z"
        fill="#fafafa"
        stroke="#d9d9d9"
      />
    </svg>
    <span>No data</span>
  </div>
);

const formatApiDate = function(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return year + '-' + month + '-' + day;
};

const getDefaultDates = function() {
  const today = new Date();
  const oneMonthAgo = new Date();
  oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
  return {
    fromDate: formatApiDate(oneMonthAgo),
    toDate: formatApiDate(today),
  };
};

const CommissionDetailsPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const clientId = searchParams.get('clientId');
  const clientName = searchParams.get('clientName');

  const defaultDates = getDefaultDates();
  const [page, setPage] = useState(1);
  const [fromDate, setFromDate] = useState(defaultDates.fromDate);
  const [toDate, setToDate] = useState(defaultDates.toDate);
  const limit = 50;

  const queryParams = {
    clientId,
    startDate: fromDate,
    endDate: toDate,
    page,
    limit
  };

  const { data: response, isLoading, error: apiError } = useGetCommissionDetailsQuery(queryParams, {
    skip: !clientId
  });

  const entries = response?.data?.entries || [];
  const pagination = response?.data?.pagination || { page: 1, limit: 50, total: 0, totalPages: 1 };
  const summary = response?.data?.summary || {
    totalCommPlus: 0,
    totalCommMinus: 0,
    totalNet: 0,
    totalEntries: 0
  };

  return (
    <div className="w-full bg-gray-100 min-h-screen">
      <style>{`
        .comm-mila-main  { background: #16a34a !important; color: #fff !important; }
        .comm-dena-main  { background: #dc2626 !important; color: #fff !important; }
        .comm-dark-th    { background: #2c3e50 !important; color: #fff !important; }
      `}</style>
      <div className="w-full">
        <div className="bg-white">
          {/* Header */}
          <div className="bg-[#2c3e50] px-5 py-3 flex items-center justify-between">
            <span className="text-xl text-white font-normal">
              Commission Details - {clientName}
            </span>
            <button
              onClick={function() { navigate(-1); }}
              className="bg-blue-500 text-white px-6 py-1.5 hover:bg-blue-600 text-sm"
            >
              Back
            </button>
          </div>

          {/* Filters Row */}
          <div className="px-5 py-4 flex items-center gap-4 bg-white border-b">
            <div className="flex items-center gap-2">
              <input
                type="date"
                value={fromDate}
                onChange={function(e) { setFromDate(e.target.value); }}
                className="px-3 py-1.5 border border-gray-300 text-sm"
              />
              <input
                type="date"
                value={toDate}
                onChange={function(e) { setToDate(e.target.value); }}
                className="px-3 py-1.5 border border-gray-300 text-sm"
              />
            </div>
          </div>

          {/* Error Message */}
          {apiError && (
            <div className="px-5 py-3 bg-red-50">
              <div className="text-red-600 text-sm">{apiError?.data?.message || 'Failed to fetch commission details'}</div>
            </div>
          )}

          {/* Table */}
          <div className="overflow-x-auto">
            {isLoading ? (
              <div className="flex justify-center py-12">
                <Loading />
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  {/* Main Header Row */}
                  <tr className="text-white">
                    <th rowSpan="2" className="comm-dark-th p-3 border border-gray-600 text-left font-medium">Date</th>
                    <th rowSpan="2" className="comm-dark-th p-3 border border-gray-600 text-left font-medium">Title</th>
                    <th colSpan="5" className="comm-mila-main p-3 border border-gray-600 text-center font-bold">MILA HAI</th>
                    <th colSpan="5" className="comm-dena-main p-3 border border-gray-600 text-center font-bold">DENA HAI</th>
                  </tr>
                  {/* Sub Header Row */}
                  <tr>
                    <th className="comm-dark-th p-2 border border-gray-600 text-left font-medium">M.Comm</th>
                    <th className="comm-dark-th p-2 border border-gray-600 text-left font-medium">S.Comm</th>
                    <th className="comm-dark-th p-2 border border-gray-600 text-left font-medium">MTK.Comm</th>
                    <th className="comm-dark-th p-2 border border-gray-600 text-left font-medium">Casino</th>
                    <th className="comm-dark-th p-2 border border-gray-600 text-left font-medium">T.Comm</th>
                    <th className="comm-dark-th p-2 border border-gray-600 text-left font-medium">M.Comm</th>
                    <th className="comm-dark-th p-2 border border-gray-600 text-left font-medium">S.Comm</th>
                    <th className="comm-dark-th p-2 border border-gray-600 text-left font-medium">MTK.Comm</th>
                    <th className="comm-dark-th p-2 border border-gray-600 text-left font-medium">Casino</th>
                    <th className="comm-dark-th p-2 border border-gray-600 text-left font-medium">T.Comm</th>
                  </tr>
                </thead>
                <tbody className="bg-white">
                  {entries.length === 0 ? (
                    <tr>
                      <td colSpan="12" className="border border-gray-300 py-8">
                        <EmptyState />
                      </td>
                    </tr>
                  ) : (
                    <>
                      {entries.map(function(entry, index) {
                        const rowBgClass = index % 2 === 0 ? 'bg-white' : 'bg-gray-50';
                        return (
                          <tr key={entry.groupKey} className={rowBgClass}>
                            <td className="p-3 border border-gray-300 text-gray-800">
                              {new Date(entry.settledAt).toLocaleDateString('en-GB').replace(/\//g, '-')}
                            </td>
                            <td className="p-3 border border-gray-300 text-gray-800">
                              {entry?.title}
                            </td>
                            {/* MILA HAI columns */}
                            <td className="p-3 border border-gray-300 text-right text-green-600">
                              {entry.matchCommPlus > 0 ? entry.matchCommPlus.toFixed(2) : 0}
                            </td>
                            <td className="p-3 border border-gray-300 text-right text-green-600">
                              {entry.sessionCommPlus > 0 ? entry.sessionCommPlus.toFixed(2) : 0}
                            </td>
                            <td className="p-3 border border-gray-300 text-right text-green-600">
                              0
                            </td>
                            <td className="p-3 border border-gray-300 text-right text-green-600">
                              {entry.casinoCommPlus > 0 ? entry.casinoCommPlus.toFixed(2) : 0}
                            </td>
                            <td className="p-3 border border-gray-300 text-right text-green-600 font-medium">
                              {entry.commPlus > 0 ? entry.commPlus.toFixed(2) : 0}
                            </td>
                            {/* DENA HAI columns */}
                            <td className="p-3 border border-gray-300 text-right text-red-600">
                              {entry.matchCommMinus > 0 ? entry.matchCommMinus.toFixed(2) : 0}
                            </td>
                            <td className="p-3 border border-gray-300 text-right text-red-600">
                              {entry.sessionCommMinus > 0 ? entry.sessionCommMinus.toFixed(2) : 0}
                            </td>
                            <td className="p-3 border border-gray-300 text-right text-red-600">
                              0
                            </td>
                            <td className="p-3 border border-gray-300 text-right text-red-600">
                              {entry.casinoCommMinus > 0 ? entry.casinoCommMinus.toFixed(2) : 0}
                            </td>
                            <td className="p-3 border border-gray-300 text-right text-red-600 font-medium">
                              {entry.commMinus > 0 ? entry.commMinus.toFixed(2) : 0}
                            </td>
                          </tr>
                        );
                      })}
                      {/* Total Row */}
                      <tr className="bg-gray-100 font-semibold">
                        <td colSpan="2" className="p-3 border border-gray-300 text-right">
                          Total ({summary.totalEntries} entries)
                        </td>
                        <td className="p-3 border border-gray-300 text-right text-green-600">
                          {Number(summary.totalCommPlus || 0).toFixed(2)}
                        </td>
                        <td className="p-3 border border-gray-300 text-right text-green-600">0</td>
                        <td className="p-3 border border-gray-300 text-right text-green-600">0</td>
                        <td className="p-3 border border-gray-300 text-right text-green-600">0</td>
                        <td className="p-3 border border-gray-300 text-right text-green-600">
                          {Number(summary.totalCommPlus || 0).toFixed(2)}
                        </td>
                        <td className="p-3 border border-gray-300 text-right text-red-600">
                          {Number(summary.totalCommMinus || 0).toFixed(2)}
                        </td>
                        <td className="p-3 border border-gray-300 text-right text-red-600">0</td>
                        <td className="p-3 border border-gray-300 text-right text-red-600">0</td>
                        <td className="p-3 border border-gray-300 text-right text-red-600">0</td>
                        <td className="p-3 border border-gray-300 text-right text-red-600">
                          {Number(summary.totalCommMinus || 0).toFixed(2)}
                        </td>
                      </tr>
                    </>
                  )}
                </tbody>
              </table>
            )}
          </div>

          {/* Pagination */}
          <div className="px-5 py-4 border-t border-gray-300 flex items-center justify-between">
            <div className="text-sm text-gray-600">
              Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} entries
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setPage(page - 1)}
                disabled={page === 1}
                className={`px-3 py-1 border ${
                  page === 1
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                Previous
              </button>
              <div className="flex gap-1">
                {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map((pageNum) => (
                  <button
                    key={pageNum}
                    onClick={() => setPage(pageNum)}
                    className={`px-3 py-1 border ${
                      pageNum === page
                        ? 'bg-blue-500 text-white'
                        : 'bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {pageNum}
                  </button>
                ))}
              </div>
              <button
                onClick={() => setPage(page + 1)}
                disabled={page === pagination.totalPages}
                className={`px-3 py-1 border ${
                  page === pagination.totalPages
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommissionDetailsPage;
